/**
 * Cross-origin auth handoff for the Classic / New shell toggle.
 *
 * The two shells live on separate origins (e.g. `app.radiolinq.com` and
 * `appv2.radiolinq.com`). Auth lives in `localStorage`, which is per-origin,
 * so a naked redirect would strand the user at the login page on the other
 * shell. This module bridges the session across the redirect.
 *
 * v1 (this file) — frontend-only: we copy the user's existing
 *   `Authorization` token, the devise-token-auth `auth` header bundle, and
 *   the cached `user` JSON into a URL **fragment** (`#p=<base64>`). The
 *   receiving shell writes them straight back into `localStorage` and
 *   reloads. Fragments are never transmitted to servers (no proxy / CDN /
 *   access logs), and `history.replaceState` scrubs the URL on arrival
 *   before anything else can read it.
 *
 * v2 (future) — backend-assisted: the backend mints a single-use, short-
 *   lived `ht` token (`POST /auth/handoff`) and the receiver exchanges it
 *   (`POST /auth/handoff/consume`). The call sites (`AppHeader` toggle,
 *   `HandoffPage`) do not change — only the two functions in this file do.
 */

const FRAGMENT_KEY = "p";

// How long a handoff URL stays valid after it's minted. Two minutes is more
// than enough for a same-tab redirect, and short enough that a stale URL
// pasted somewhere later can't be replayed.
const MAX_AGE_MS = 2 * 60 * 1000;

interface HandoffPayload {
  /** localStorage["Authorization"] value (raw token string). */
  t: string;
  /** localStorage["auth"] parsed: devise-token-auth headers. */
  a: Record<string, string>;
  /** localStorage["user"] parsed: plain JSON of the active user. */
  u: unknown;
  /** Path on the target shell to land on after handoff. */
  r: string;
  /** Mint time, ms since epoch — used for freshness check. */
  ts: number;
}

function utf8Base64Encode(value: string): string {
  // btoa is latin-1 only; round-trip through UTF-8 bytes first so non-ASCII
  // names / emails in the user payload don't blow up.
  const bytes = new TextEncoder().encode(value);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
}

function utf8Base64Decode(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Defensive normalisation for the post-handoff return path. We never want
 * a handoff URL to be able to redirect the user to an off-site location
 * (e.g. `//evil.com/...`) or inject a header-split, so anything that
 * doesn't look like an in-app path collapses to `"/"`.
 */
function sanitizeReturnPath(value: unknown): string {
  if (typeof value !== "string") return "/";
  if (!value.startsWith("/")) return "/";
  if (value.startsWith("//")) return "/";
  if (value.includes("\n") || value.includes("\r")) return "/";
  return value;
}

/**
 * Build the cross-origin handoff URL for `targetBase`, or return `null` if
 * there's nothing meaningful to hand off (e.g. no `Authorization` token in
 * localStorage — the caller should then fall back to a bare redirect, which
 * will land the user at the login page on the other shell).
 */
export function buildHandoffUrl(
  targetBase: string,
  returnPath: string
): string | null {
  if (!targetBase) return null;

  const token = localStorage.getItem("Authorization");
  const authRaw = localStorage.getItem("auth");
  const userRaw = localStorage.getItem("user");
  if (!token || !authRaw || !userRaw) return null;

  let authHeaders: Record<string, string>;
  let user: unknown;
  try {
    authHeaders = JSON.parse(authRaw);
    user = JSON.parse(userRaw);
  } catch (_) {
    return null;
  }

  const payload: HandoffPayload = {
    t: token,
    a: authHeaders,
    u: user,
    r: sanitizeReturnPath(returnPath),
    ts: Date.now(),
  };

  let b64: string;
  try {
    b64 = utf8Base64Encode(JSON.stringify(payload));
  } catch (_) {
    return null;
  }

  const cleanBase = targetBase.replace(/\/+$/, "");
  return `${cleanBase}/auth/handoff#${FRAGMENT_KEY}=${b64}`;
}

export interface ConsumeResult {
  ok: boolean;
  /** Sanitized path to navigate to after a successful handoff. */
  returnPath: string;
}

/**
 * Read the handoff payload from `location.hash`, validate freshness, write
 * the auth state into `localStorage`, and scrub the URL. Does NOT navigate
 * or touch the redux store — the caller is expected to `window.location
 * .replace(returnPath)` so that `App.tsx`'s boot-time `AUTHENTICATED`
 * dispatch picks up the freshly written localStorage cleanly.
 */
export function consumeHandoffFromHash(): ConsumeResult {
  const failed: ConsumeResult = { ok: false, returnPath: "/" };

  const rawHash = window.location.hash || "";
  const hash = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
  if (!hash) return failed;

  const params = new URLSearchParams(hash);
  const b64 = params.get(FRAGMENT_KEY);
  if (!b64) return failed;

  let payload: HandoffPayload;
  try {
    payload = JSON.parse(utf8Base64Decode(b64));
  } catch (_) {
    return failed;
  }

  if (
    !payload ||
    typeof payload.t !== "string" ||
    !payload.t ||
    !payload.a ||
    typeof payload.a !== "object" ||
    !payload.u ||
    typeof payload.ts !== "number"
  ) {
    return failed;
  }
  if (Date.now() - payload.ts > MAX_AGE_MS) return failed;

  try {
    localStorage.setItem("Authorization", payload.t);
    localStorage.setItem("auth", JSON.stringify(payload.a));
    localStorage.setItem("user", JSON.stringify(payload.u));
  } catch (_) {
    return failed;
  }

  // Strip the fragment so the back-button, browser history, and any
  // accidental share don't expose the bridged token.
  try {
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", cleanUrl);
  } catch (_) {
    /* best-effort */
  }

  return { ok: true, returnPath: sanitizeReturnPath(payload.r) };
}
